import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { asText } from "@/hq/asText";
import PitchDetailModal from "@/hq/campaigns/PitchDetailModal";
import {
  Send, Mail, MousePointerClick, CheckCircle2, XCircle, Zap, Undo2,
} from "lucide-react";

/**
 * Phase 3I.4Q.3 — Shared canonical Pitch/Campaign Activity Feed.
 *
 * Reads from `/api/hq/control/activity/feed` — the single source of
 * truth consumed by:
 *   • Campaigns → Activity tab (full feed)
 *   • Dashboard → Recent Activity (compact mode)
 *   • Client Profile → Pitch & Campaign History (business_id scoped)
 *
 * Never maintains its own counters or independent event streams.
 */

const CANONICAL_TYPES = [
  { v: "", l: "All events" },
  { v: "PITCH_SENT", l: "Pitch sent" },
  { v: "PITCH_OPENED", l: "Pitch opened" },
  { v: "PITCH_CLICKED", l: "Pitch clicked" },
  { v: "PITCH_INTERESTED", l: "Marked Interested" },
  { v: "PITCH_INTERESTED_CLEARED", l: "Interested cleared" },
  { v: "CAMPAIGN_SENT", l: "Campaign sent" },
  { v: "CAMPAIGN_OPENED", l: "Campaign opened" },
  { v: "CAMPAIGN_CLICKED", l: "Campaign clicked" },
  { v: "CAMPAIGN_UNSUBSCRIBED", l: "Unsubscribed" },
];

const ICON_BY_TYPE = {
  PITCH_SENT: Send,
  PITCH_OPENED: Mail,
  PITCH_CLICKED: MousePointerClick,
  PITCH_INTERESTED: CheckCircle2,
  PITCH_INTERESTED_CLEARED: Undo2,
  PITCH_FAILED: XCircle,
  CAMPAIGN_SENT: Send,
  CAMPAIGN_OPENED: Mail,
  CAMPAIGN_CLICKED: MousePointerClick,
  CAMPAIGN_UNSUBSCRIBED: XCircle,
};

const TONE_BY_TYPE = {
  PITCH_INTERESTED:         "text-[#0F7B3E]",
  PITCH_INTERESTED_CLEARED: "text-[#8A8A8A]",
  PITCH_CLICKED:            "text-[#FF6A00]",
  CAMPAIGN_CLICKED:         "text-[#FF6A00]",
  PITCH_FAILED:             "text-[#C1350B]",
  CAMPAIGN_UNSUBSCRIBED:    "text-[#C1350B]",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const shortFmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

function humanLabel(type) {
  return (type || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function HQActivityFeed({
  businessId = null,
  compact = false,
  limit = null,
  showFilters = true,
  testIdPrefix = "hq-activity-feed",
}) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activityType, setActivityType] = useState("");
  const [pitchType, setPitchType] = useState("");
  const [q, setQ] = useState("");
  const [selectedComm, setSelectedComm] = useState(null);
  const navigate = useNavigate();

  const pageSize = compact ? (limit || 10) : 25;

  const load = () => {
    setItems(null);
    const params = { page, page_size: pageSize };
    if (businessId) params.business_id = businessId;
    if (activityType) params.activity_type = activityType;
    if (pitchType) params.pitch_type = pitchType;
    if (q) params.q = q;
    hqApi
      .get("/control/activity/feed", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => setItems([]));
  };

  useEffect(load, [page, activityType, pitchType, businessId]);
  useEffect(() => {
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  const openRow = (row) => {
    if (row.communication_id) setSelectedComm(row.communication_id);
    else if (row.business_id) navigate(`/hq/clients/${row.business_id}`);
  };

  const openClient = (biz_id, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (biz_id) navigate(`/hq/clients/${biz_id}`);
  };

  return (
    <>
      {showFilters && !compact && (
        <Card className="p-4 mb-3" data-testid={`${testIdPrefix}-filters`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              data-testid={`${testIdPrefix}-search`}
              className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px]"
              placeholder="Search business, contact or email…"
              value={q}
              onChange={(e) => { setPage(1); setQ(e.target.value); }}
            />
            <select
              data-testid={`${testIdPrefix}-filter-type`}
              value={activityType}
              onChange={(e) => { setPage(1); setActivityType(e.target.value); }}
              className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px] bg-white"
            >
              {CANONICAL_TYPES.map((t) => <option key={t.v || "all"} value={t.v}>{t.l}</option>)}
            </select>
            <select
              data-testid={`${testIdPrefix}-filter-pitch-type`}
              value={pitchType}
              onChange={(e) => { setPage(1); setPitchType(e.target.value); }}
              className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[13px] bg-white"
            >
              <option value="">All pitch types</option>
              <option value="website">Website</option>
              <option value="optimise">Optimise</option>
              <option value="crm">CRM</option>
              <option value="full">Full Pitch</option>
            </select>
          </div>
          <div className="mt-2 text-[11px] tracking-[0.16em] uppercase font-bold text-[#8A8A8A]"
               data-testid={`${testIdPrefix}-total`}>{total} events</div>
        </Card>
      )}

      {items === null ? (
        <div className="p-4"><Skeleton h={compact ? 100 : 200}/></div>
      ) : items.length === 0 ? (
        <EmptyState title="No activity yet" testId={`${testIdPrefix}-empty`}
          hint="Send a pitch or trigger a campaign to see activity here."/>
      ) : (
        <ul className={`divide-y divide-[#EAEAEA] ${compact ? "" : "bg-white rounded-xl border border-[#EAEAEA]"}`}
            data-testid={`${testIdPrefix}-list`}>
          {items.map((a) => {
            const Icon = ICON_BY_TYPE[a.canonical_type] || Send;
            const tone = TONE_BY_TYPE[a.canonical_type] || "text-[#8A4A00]";
            return (
              <li
                key={a.id}
                onClick={() => openRow(a)}
                data-testid={`${testIdPrefix}-row-${a.id}`}
                className="flex items-start gap-3 px-4 py-3 hover:bg-[#FFF7EE] cursor-pointer"
              >
                <div className={`shrink-0 mt-0.5 ${tone}`}><Icon size={16}/></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    {a.business_id ? (
                      <button
                        type="button"
                        onClick={(e) => openClient(a.business_id, e)}
                        data-testid={`${testIdPrefix}-business-${a.id}`}
                        className="font-black text-[13px] truncate hover:text-[#FF6A00] hover:underline text-left"
                      >{asText(a.business_name) || "Unknown business"}</button>
                    ) : (
                      <span className="font-black text-[13px] truncate">
                        {asText(a.business_name) || "Unknown business"}
                      </span>
                    )}
                    <span className={`text-[10px] uppercase tracking-[0.14em] font-black ${tone}`}>
                      {humanLabel(a.canonical_type || a.event_type)}
                    </span>
                  </div>
                  <div className="text-[12px] text-[#4A4A4A] truncate">
                    {a.pitch_type && (
                      <span className="uppercase font-black tracking-[0.14em] text-[#FF6A00] mr-1">
                        {a.pitch_type === "full" ? "Full Pitch" : a.pitch_type}
                      </span>
                    )}
                    {asText(a.description) || asText(a.subject) || asText(a.event_type)}
                  </div>
                  {a.url && (
                    <div className="text-[11px] text-[#8A8A8A] font-mono truncate">↳ {asText(a.url)}</div>
                  )}
                </div>
                <div className="shrink-0 text-[11px] text-[#8A8A8A] whitespace-nowrap"
                     title={fmt(a.created_at)}>
                  {compact ? shortFmt(a.created_at) : fmt(a.created_at)}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!compact && pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-[12px]"
             data-testid={`${testIdPrefix}-pagination`}>
          <div>Page {page} of {pages}</div>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-full border border-[#EAEAEA] disabled:opacity-40"
            >Prev</button>
            <button
              disabled={page >= pages}
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              className="px-3 py-1.5 rounded-full border border-[#EAEAEA] disabled:opacity-40"
            >Next</button>
          </div>
        </div>
      )}

      <PitchDetailModal
        commId={selectedComm}
        onClose={() => setSelectedComm(null)}
        onChanged={load}
      />
    </>
  );
}
